import React, { useCallback, useContext, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import api from "../../services/api.js";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError.js";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer/index.js";
import MainHeader from "../../components/MainHeader/index.js";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title/index.js";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";

const SUPPORT_USER = "Suporte_Centerchat";
const POLL_INTERVAL_MS = 5000;

const STATUS_COLORS = {
	pending: "#9e9e9e",
	processing: "#1976d2",
	completed: "#2e7d32",
	error: "#d32f2f",
};

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "auto",
	},
	folderName: {
		fontFamily: "monospace",
		fontSize: 13,
	},
	errorText: {
		display: "block",
		color: theme.palette.error.main,
		fontSize: 12,
		marginTop: 4,
	},
	actions: {
		whiteSpace: "nowrap",
	},
}));

const formatDate = value => (value ? format(parseISO(value), "dd/MM/yyyy HH:mm") : "-");

const formatCount = value => (value !== null && value !== undefined ? value : "-");

const Backups = () => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const isSupport = user?.name === SUPPORT_USER;

	const [backups, setBackups] = useState([]);
	const [loading, setLoading] = useState(true);
	const [creating, setCreating] = useState(false);
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);

	const loadBackups = useCallback(async ({ silent = false } = {}) => {
		try {
			const { data } = await api.get("/backups");
			setBackups(Array.isArray(data) ? data : []);
		} catch (err) {
			if (!silent) toastError(err);
		} finally {
			setLoading(false);
		}
	}, []);

	const hasRunningBackup = backups.some(
		backup => backup.status === "pending" || backup.status === "processing"
	);

	useEffect(() => {
		if (!isSupport) return;
		loadBackups();
	}, [isSupport, loadBackups]);

	useEffect(() => {
		if (!isSupport || !hasRunningBackup) return undefined;
		const timer = setInterval(() => loadBackups({ silent: true }), POLL_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [isSupport, hasRunningBackup, loadBackups]);

	if (!isSupport) {
		return <Redirect to="/" />;
	}

	const handleCreateBackup = async () => {
		setCreateModalOpen(false);
		try {
			setCreating(true);
			await api.post("/backups");
			toast.success(i18n.t("backups.create_started"));
			await loadBackups();
		} catch (err) {
			toastError(err);
		} finally {
			setCreating(false);
		}
	};

	const fetchDownloadUrl = async backup => {
		const { data } = await api.get(`/backups/${backup.id}/download-link`);
		return data.downloadUrl;
	};

	const handleOpenLink = async backup => {
		try {
			const url = await fetchDownloadUrl(backup);
			window.open(url, "_blank", "noopener");
		} catch (err) {
			toastError(err);
		}
	};

	const handleCopyLink = async backup => {
		try {
			const url = await fetchDownloadUrl(backup);
			await navigator.clipboard.writeText(url);
			toast.success(i18n.t("backups.link_copied"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await api.delete(`/backups/${deleteTarget.id}`);
			toast.success(i18n.t("backups.deleted_successfully"));
			setDeleteTarget(null);
			loadBackups();
		} catch (err) {
			toastError(err);
		}
	};

	const isRunning = backup => backup.status === "pending" || backup.status === "processing";

	return (
		<MainContainer>
			<ConfirmationModal
				title={i18n.t("backups.create_title")}
				open={createModalOpen}
				onClose={() => setCreateModalOpen(false)}
				onConfirm={handleCreateBackup}
			>
				{i18n.t("backups.create_confirm")}
			</ConfirmationModal>

			<ConfirmationModal
				title={i18n.t("backups.delete")}
				open={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleDelete}
			>
				{i18n.t("backups.confirm_delete")}
			</ConfirmationModal>

			<MainHeader>
				<Title>{i18n.t("backups.title")}</Title>
				<MainHeaderButtonsWrapper>
					<Button
						variant="outlined"
						color="primary"
						onClick={() => loadBackups()}
					>
						{i18n.t("backups.refresh")}
					</Button>
					<Button
						variant="contained"
						color="primary"
						disabled={creating || hasRunningBackup}
						onClick={() => setCreateModalOpen(true)}
					>
						{i18n.t("backups.create_new")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<Typography variant="body2" color="textSecondary" paragraph>
				{i18n.t("backups.description")}
			</Typography>

			<Paper className={classes.mainPaper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center">{i18n.t("backups.name")}</TableCell>
							<TableCell align="center">{i18n.t("backups.status")}</TableCell>
							<TableCell align="center">{i18n.t("backups.messages")}</TableCell>
							<TableCell align="center">{i18n.t("backups.contacts")}</TableCell>
							<TableCell align="center">{i18n.t("backups.tickets")}</TableCell>
							<TableCell align="center">{i18n.t("backups.files")}</TableCell>
							<TableCell align="center">{i18n.t("backups.size")}</TableCell>
							<TableCell align="center">{i18n.t("backups.created_at")}</TableCell>
							<TableCell align="center">{i18n.t("backups.expires_at")}</TableCell>
							<TableCell align="center">{i18n.t("backups.actions")}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton />
						) : backups.length === 0 ? (
							<TableRow>
								<TableCell colSpan={10} align="center">
									<Typography variant="body1">{i18n.t("backups.no_backups")}</Typography>
									<Typography variant="body2" color="textSecondary">
										{i18n.t("backups.no_backups_hint")}
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							backups.map(backup => (
								<TableRow key={backup.id}>
									<TableCell align="center" className={classes.folderName}>
										{backup.backupName}
									</TableCell>
									<TableCell align="center">
										<Chip
											size="small"
											label={i18n.t(`backups.${backup.status}`)}
											style={{
												backgroundColor: STATUS_COLORS[backup.status],
												color: "#fff",
											}}
										/>
										{backup.status === "error" && backup.errorMessage && (
											<span className={classes.errorText}>{backup.errorMessage}</span>
										)}
									</TableCell>
									<TableCell align="center">{formatCount(backup.totalMessages)}</TableCell>
									<TableCell align="center">{formatCount(backup.totalContacts)}</TableCell>
									<TableCell align="center">{formatCount(backup.totalTickets)}</TableCell>
									<TableCell align="center">{formatCount(backup.totalFiles)}</TableCell>
									<TableCell align="center">{backup.backupSize || "-"}</TableCell>
									<TableCell align="center">{formatDate(backup.createdAt)}</TableCell>
									<TableCell align="center">{formatDate(backup.expiresAt)}</TableCell>
									<TableCell align="center" className={classes.actions}>
										{backup.status === "completed" && (
											<>
												<Tooltip title={i18n.t("backups.open_link")}>
													<IconButton size="small" onClick={() => handleOpenLink(backup)}>
														<OpenInNewIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title={i18n.t("backups.copy_link")}>
													<IconButton size="small" onClick={() => handleCopyLink(backup)}>
														<FileCopyIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</>
										)}
										<Tooltip title={i18n.t("backups.delete")}>
											<span>
												<IconButton
													size="small"
													disabled={isRunning(backup)}
													onClick={() => setDeleteTarget(backup)}
												>
													<DeleteOutlineIcon fontSize="small" />
												</IconButton>
											</span>
										</Tooltip>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</Paper>
		</MainContainer>
	);
};

export default Backups;
